/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-shadow */
import './TQProfileCompleteQuestions.scss';

import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import ConfirmationModal
  from '../../../../components/Modals/ConfirmationModal/ConfirmationModal';
import {
  useAppNavigate,
  useAppSelector,
} from '../../../../shared/hooks';
import {
  getTQProfileQueryAttemptStatus,
} from '../../../../shared/tqConnectUtils';
import { QuestionAttemptStatus } from '../../../../types/assignmentTypes';
import { ROUTES } from '../../../../types/routes';
import { TQProfileQuestionType } from '../../../../types/tqConnectTypes';
import TQConnectIcon from '../TQConnectIcon';
import ProfileCompletion from './ProfileCompletion';
import {
  SubmitTQProfileProvider,
  useSubmitTQProfileContext,
} from './SubmitTQProfileContextProvider';

interface Props {
  userId: string;
  userProfilePicUrl: string;
  title?: React.ReactNode;
  isOpen: boolean;
  initialQuestionNo?: number;
  onClose: () => void;
  onSubmitSuccess?: () => void;
}

const TQProfileCompleteQuestionItem: React.FC<Partial<Props>> = ({
  initialQuestionNo,
}) => {
  const navigate = useAppNavigate();
  const userDetails = useAppSelector((state) => state.user);
  const {
    isSaving,
    goToPrevOrNextQuestion,
    tqProfileCompletionQueries,
    questionsMapByIndex,
    questionsMap,
    currentIndex,
    saveQuestionAnswer,
    setCurrentIndex,
    goToNextUnattemptedQuestion,
    closeSubmitTQProfileModal,
    hasPrevQuestion,
    showCompletionScreen,
  } = useSubmitTQProfileContext();

  const [isExitConfirmationVisible, setIsExitConfirmationVisible] =
    useState(false);

  const isLastQuestion =
    tqProfileCompletionQueries.length === 0 ||
    currentIndex === tqProfileCompletionQueries.length - 1;
  const currentQuestion = questionsMapByIndex[currentIndex];

  const {
    _id: questionId = '',
    order = 1,
    question: questionTitle,
    type,
    score,
    options,
    selectedOptions,
    subjectiveAnswer,
    enableMultipleSelection,
    helpText,
  } = currentQuestion || {};

  const hasSkippedAllQuestions =
    tqProfileCompletionQueries.length === 0 ||
    tqProfileCompletionQueries.every(
      (q) =>
        getTQProfileQueryAttemptStatus(questionsMap[q._id]) ===
        QuestionAttemptStatus.SKIPPED,
    );

  const hasQuestionAnswer = useMemo(() => {
    return (
      (type !== TQProfileQuestionType.TEXT && selectedOptions?.length) ||
      (type === TQProfileQuestionType.TEXT && subjectiveAnswer?.trim().length)
    );
  }, [type, selectedOptions, subjectiveAnswer]);

  const renderQuestion = () => {
    if (
      (type === TQProfileQuestionType.MULTIPLE_CHOICE ||
        type === TQProfileQuestionType.DROPDOWN) &&
      !enableMultipleSelection &&
      options?.length
    ) {
      return (
        <div className="tq-profile-questions__options tq-profile-questions__options--single">
          {options &&
            options?.map((opt) => (
              <label
                key={opt}
                className={`tq-profile-questions__option ${selectedOptions?.includes(opt) ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name={currentQuestion._id}
                  value={opt}
                  checked={selectedOptions?.includes(opt)}
                  onChange={() =>
                    saveQuestionAnswer(questionId, 'save', {
                      questionId,
                      selectedOptions: Array.isArray(opt) ? opt : [opt],
                    })
                  }
                />
                <span className="tq-profile-questions__option-label">
                  {opt}
                </span>
              </label>
            ))}
        </div>
      );
    }

    if (
      (type === TQProfileQuestionType.MULTIPLE_CHOICE ||
        type === TQProfileQuestionType.DROPDOWN) &&
      enableMultipleSelection &&
      options?.length
    ) {
      return (
        <div className="tq-profile-questions__options tq-profile-questions__options--multi">
          {options?.map((opt) => (
            <label
              key={opt}
              className={`tq-profile-questions__option ${selectedOptions?.includes(opt) ? 'selected' : ''}`}>
              <input
                type="checkbox"
                value={opt}
                checked={selectedOptions?.includes(opt)}
                onChange={() => {
                  const newSelectedOptions = selectedOptions?.includes(opt)
                    ? selectedOptions?.filter((o) => o !== opt)
                    : [...(selectedOptions || []), opt];
                  saveQuestionAnswer(questionId, 'save', {
                    questionId,
                    selectedOptions: newSelectedOptions,
                  });
                }}
              />
              <span className="tq-profile-questions__option-label">{opt}</span>
            </label>
          ))}
        </div>
      );
    }

    if (currentQuestion?.type === TQProfileQuestionType.TEXT) {
      return (
        <div className="tq-profile-questions__text-wrapper" key={questionId}>
          <textarea
            className={`tq-profile-questions__textarea ${subjectiveAnswer?.trim().length ? 'filled' : ''}`}
            placeholder={helpText || 'Type your answer'}
            value={subjectiveAnswer}
            onChange={(e) =>
              saveQuestionAnswer(
                questionId,
                'save',
                {
                  questionId,
                  subjectiveAnswer: e.target.value,
                },
                false,
              )
            }
            rows={5}
          />
        </div>
      );
    }
  };

  useEffect(() => {
    setCurrentIndex(initialQuestionNo ? initialQuestionNo - 1 : 0);
  }, [setCurrentIndex, initialQuestionNo]);

  if (showCompletionScreen) {
    return (
      <div className="tq-profile-questions">
        <ProfileCompletion
          openUserProfile={() => {
            navigate(
              ROUTES.USER_PROFILE,
              { userId: userDetails.id || '' },
              { replace: true },
            );
          }}
          openRequestsDiscovery={() => {
            navigate(
              ROUTES.TQ_CONNECT_SERVICE_REQUESTS_DISCOVER,
              {},
              { replace: true },
            );
          }}
        />
      </div>
    );
  }

  return (
    <div className="tq-profile-questions">
      <header className="tq-profile-questions__header">
        <button
          type="button"
          className="tq-profile-questions__close"
          onClick={() => {
            navigate.goBack();
            closeSubmitTQProfileModal();
          }}
          aria-label="Close">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 4L4 12M4 4l8 8"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <span className="tq-profile-questions__step">
          Question {order}/{tqProfileCompletionQueries.length}
        </span>
        <span className="tq-profile-questions__pill">
          <TQConnectIcon /> {score} TQ
        </span>
      </header>

      <div className="tq-profile-questions__body">
        <h1 className="tq-profile-questions__title">{questionTitle}</h1>
        {helpText && (
          <p className="tq-profile-questions__subtitle">{helpText}</p>
        )}
        {renderQuestion()}
      </div>

      <footer className="tq-profile-questions__footer">
        <button
          type="button"
          className="tq-profile-questions__btn tq-profile-questions__btn--back"
          onClick={() => goToPrevOrNextQuestion('prev')}
          disabled={!hasPrevQuestion}>
          Back
        </button>
        {hasQuestionAnswer ? (
          <button
            type="button"
            className="tq-profile-questions__btn tq-profile-questions__btn--submit"
            disabled={isSaving}
            onClick={() => {
              saveQuestionAnswer(
                questionId,
                'submit',
                type === TQProfileQuestionType.TEXT
                  ? {
                      questionId: questionId || '',
                      subjectiveAnswer: subjectiveAnswer,
                    }
                  : {
                      questionId: questionId || '',
                      selectedOptions: selectedOptions,
                    },
                true,
              );
            }}>
            Submit & Earn <TQConnectIcon /> {score} TQ
          </button>
        ) : (
          <button
            type="button"
            className="tq-profile-questions__btn tq-profile-questions__btn--skip"
            onClick={() => {
              if (hasSkippedAllQuestions && isLastQuestion) {
                setIsExitConfirmationVisible(true);
              } else {
                goToNextUnattemptedQuestion();
              }
            }}>
            Skip
          </button>
        )}
      </footer>
      <ConfirmationModal
        open={isExitConfirmationVisible}
        message={`You haven't answered any questions yet. Are you sure you want to leave without completing your profile?`}
        cancelButtonText="Yes, will complete it later"
        okayButtonText="Continue"
        title="Hold it right there!"
        handleOk={() => {
          setIsExitConfirmationVisible(false);
          // go to the first question
          setCurrentIndex(0);
        }}
        handleCancel={() => {
          setIsExitConfirmationVisible(false);
          // close the modal if chooses to exit
          closeSubmitTQProfileModal();
          navigate.goBack();
        }}
      />
    </div>
  );
};

const TQProfileCompleteQuestions: React.FC = () => {
  const userDetails = useAppSelector((state) => state.user);
  const searchParams = new URLSearchParams(window.location.search);
  const initialQuestionNo = searchParams.get('initialQuestionNo');

  const initialCurrentQuestionNo =
    initialQuestionNo && !Number.isNaN(parseInt(initialQuestionNo, 10))
      ? parseInt(initialQuestionNo, 10)
      : undefined;

  return (
    <SubmitTQProfileProvider
      userId={userDetails.id || ''}
      onClose={() => {}}
      onSubmitSuccess={() => {}}>
      <TQProfileCompleteQuestionItem
        initialQuestionNo={initialCurrentQuestionNo}
      />
    </SubmitTQProfileProvider>
  );
};

export default TQProfileCompleteQuestions;
